import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import type { CSSProperties, FocusEvent, KeyboardEvent, Ref } from 'react'
import { cn } from '../../lib/cn'
import { motionMs } from '../../lib/reducedMotion'
import type { RoundMediaItem } from '../../lib/roundMedia'
import { roundMediaContentPath, roundMediaPosterPath } from '../../lib/roundMedia'
import {
  VIDEO_CONTROL_FADE_MS,
  VIDEO_CONTROL_IDLE_MS,
  controlsHidden,
  controlsMayFade,
  effectiveDuration,
  formatPlaybackTime,
  initialPlaybackState,
  progressFraction,
  reducePlayback,
  showLoading,
  shownPosition,
  soundAction,
  surfaceAction,
  type PlaybackEffect,
  type PlaybackEvent,
  type SurfaceAction,
} from '../../lib/roundMediaPlayback'
import {
  ExitFullscreenMark,
  FullscreenMark,
  PauseMark,
  PlayMark,
  ReplayMark,
  SoundMark,
} from './mediaPlayerMarks'

/**
 * The GTG GC web viewer's video player — the web host for the playback rules
 * in `lib/roundMediaPlayback.ts`, which Caddie's native player shares.
 *
 * The reducer decides what every tap and every `<video>` event means; this
 * component only feeds it the element's events and carries out the effects it
 * returns against the element. What is web-specific lives here: the browser
 * queues a `play()` issued before data arrives, so the reducer runs with
 * `deferPlayUntilReady: false`; a `<video>` reports a stall as `waiting`,
 * never as `pause`, so every `pause` it fires is deliberate and is reported
 * as such; the browser cannot say whether a screen reader is running, so the
 * rail is pinned while hovered or focused instead, any key wakes it, and a
 * faded rail is gone from sight only — never `aria-hidden` or `inert`; and
 * delivery is a same-origin path that answers `Cache-Control: no-store` and
 * redirects to a signed URL minted per request, so a failure's Retry needs no
 * cache-buster — remounting the element (`key={attempt}`) on the same bare
 * paths re-requests both the content and the poster.
 *
 * Rules the section's viewer must keep for this to hold: the dialog feeds
 * every keydown to `shortcut`, closing calls `pause` before unmounting, and
 * in full screen everything of the dialog outside the player is `inert`, so
 * the layer's focus scope is the player's own controls.
 */
export interface RoundMediaVideoPlayerHandle {
  /** Stop playback without closing anything — the viewer calls it as it closes. */
  pause(): void
  /** The viewer's keyboard map, fed every keydown the open dialog sees. */
  shortcut(event: KeyboardEvent<HTMLElement>): void
}

export interface RoundMediaVideoPlayerProps {
  item: RoundMediaItem
  /** The sentence-cased description the section computes; the video's accessible name. */
  description: string
  fullscreen: boolean
  onFullscreenChange: (next: boolean) => void
  ref?: Ref<RoundMediaVideoPlayerHandle>
}

/** The rail's transport is the one accessible Play / Pause / Replay control. */
const TRANSPORT_LABELS: Record<SurfaceAction, string> = {
  play: 'Play video',
  pause: 'Pause video',
  replay: 'Replay video',
}

/** A pointer that keeps moving re-arms the idle timer this often, not per event. */
const POINTER_WAKE_THROTTLE_MS = 250

function frameClassName(fullscreen: boolean): string {
  return fullscreen
    ? 'fixed inset-0 z-[60] grid place-items-center bg-ink text-paper'
    : 'relative mx-auto w-full max-h-[70vh] overflow-hidden rounded-club bg-ink text-paper'
}

/**
 * The frame is the clip's own box: its aspect ratio, as wide as the stage
 * allows and no taller than 70vh, with the width following the height cap so
 * the ink never shows as bars beside a portrait clip. Full screen drops all
 * of it and lets the element letterbox itself.
 */
function frameStyle(item: RoundMediaItem, fullscreen: boolean): CSSProperties | undefined {
  if (fullscreen) return undefined
  const dimensions = item.dimensions
  const usable = dimensions !== null && dimensions.width > 0 && dimensions.height > 0
  const width = usable ? dimensions.width : 4
  const height = usable ? dimensions.height : 5
  return {
    aspectRatio: `${width} / ${height}`,
    width: `min(100%, calc(70vh * ${width} / ${height}))`,
  }
}

function issuePlay(video: HTMLVideoElement | null): void {
  if (!video) return
  // A pause that interrupts a pending play rejects it with AbortError, which
  // is not a failure; a real one arrives as the element's own `error` event.
  // jsdom's play() returns undefined, hence the resolve.
  Promise.resolve(video.play()).catch(() => undefined)
}

/** What the frame's live region says; empty is a state too. */
function announcementFor(loading: boolean, ended: boolean): string {
  if (loading) return 'Loading video…'
  return ended ? 'Video ended' : ''
}

export function RoundMediaVideoPlayer({
  item,
  description,
  fullscreen,
  onFullscreenChange,
  ref,
}: RoundMediaVideoPlayerProps) {
  const [attempt, setAttempt] = useState(0)
  const [failed, setFailed] = useState(false)
  const frameRef = useRef<RoundMediaVideoPlayerHandle>(null)
  const retryRef = useRef<HTMLButtonElement>(null)
  /** Whether the failure took the member's focus with it — decided as the frame fails, before it unmounts. */
  const focusRetryRef = useRef(false)
  const contentPath = roundMediaContentPath(item)
  const posterPath = roundMediaPosterPath(item)

  useImperativeHandle(
    ref,
    () => ({
      pause: () => frameRef.current?.pause(),
      shortcut: (event) => frameRef.current?.shortcut(event),
    }),
    [],
  )

  const fail = useCallback(
    (focusWasInside: boolean) => {
      focusRetryRef.current = focusWasInside
      onFullscreenChange(false)
      setFailed(true)
    },
    [onFullscreenChange],
  )

  // A failure unmounts whatever the member had focused in the frame, and the
  // browser drops that focus on the body. Land it on Try again instead — and
  // only then: focus that was elsewhere is not the failure's to move
  // (WCAG 2.4.3).
  useEffect(() => {
    if (failed && focusRetryRef.current) retryRef.current?.focus()
  }, [failed])

  // The card never opens an item without a same-origin route; keep that true
  // here too rather than mount an element with nothing to load.
  if (contentPath === null) return null

  if (failed) {
    return (
      <div data-media-player="" className={frameClassName(false)} style={frameStyle(item, false)}>
        <div className="absolute inset-0 grid place-items-center p-4 text-center">
          <div>
            <p role="alert" className="text-[13px] font-semibold text-paper">
              Video could not load
            </p>
            <button
              ref={retryRef}
              type="button"
              onClick={() => {
                setFailed(false)
                setAttempt((count) => count + 1)
              }}
              className="mt-3 min-h-11 rounded-club border border-paper bg-paper px-4 text-[12px] font-semibold text-pine"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <PlayerFrame
      key={attempt}
      ref={frameRef}
      item={item}
      description={description}
      fullscreen={fullscreen}
      onFullscreenChange={onFullscreenChange}
      contentSrc={contentPath}
      posterSrc={posterPath ?? undefined}
      focusTransportOnMount={attempt > 0}
      onFail={fail}
    />
  )
}

interface PlayerFrameProps {
  item: RoundMediaItem
  description: string
  fullscreen: boolean
  onFullscreenChange: (next: boolean) => void
  contentSrc: string
  posterSrc: string | undefined
  /** After a retry the Try again button is gone; land the member on the transport. */
  focusTransportOnMount: boolean
  /** The clip failed; `focusWasInside` says whether the frame held focus as it did. */
  onFail: (focusWasInside: boolean) => void
  ref?: Ref<RoundMediaVideoPlayerHandle>
}

/**
 * One attempt at one clip: the `<video>`, the reducer state around it, and
 * the surface, veils and rail drawn from that state. The parent remounts it
 * (`key={attempt}`) after a failure, which is what gives Retry a fresh element
 * and a fresh reducer.
 */
function PlayerFrame({
  item,
  description,
  fullscreen,
  onFullscreenChange,
  contentSrc,
  posterSrc,
  focusTransportOnMount,
  onFail,
  ref,
}: PlayerFrameProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const transportRef = useRef<HTMLButtonElement>(null)
  const fullscreenToggleRef = useRef<HTMLButtonElement>(null)
  const exitRef = useRef<HTMLButtonElement>(null)
  const [state, setState] = useState(() =>
    initialPlaybackState({
      wireDurationSeconds: item.duration_seconds,
      audioPresent: item.audio_present,
      deferPlayUntilReady: false,
    }),
  )
  // The reducer's latest state for handlers and effects: an event that fires
  // while an effect runs (a pause interrupting a play) must reduce from the
  // state that effect was issued under, not from the last render.
  const stateRef = useRef(state)
  /** The rail is mid-fade: transparent, but still under the pointer. */
  const [fading, setFading] = useState(false)
  const [hudHovered, setHudHovered] = useState(false)
  const [hudFocused, setHudFocused] = useState(false)
  const lastPointerWakeRef = useRef(0)

  const runEffect = useCallback(
    (effect: PlaybackEffect) => {
      const video = videoRef.current
      switch (effect.type) {
        case 'play':
          issuePlay(video)
          return
        case 'pause':
          video?.pause()
          return
        case 'replay':
          if (video) {
            video.currentTime = 0
            issuePlay(video)
          }
          return
        case 'seek':
          if (video) video.currentTime = effect.position
          return
        case 'setMuted':
          if (video) video.muted = effect.muted
          return
        case 'fail':
          onFail(rootRef.current?.contains(document.activeElement) ?? false)
          return
      }
    },
    [onFail],
  )

  const dispatch = useCallback(
    (event: PlaybackEvent) => {
      const before = stateRef.current
      const transition = reducePlayback(before, event)
      stateRef.current = transition.state
      if (transition.state.wakeCount !== before.wakeCount) setFading(false)
      setState(transition.state)
      transition.effects.forEach(runEffect)
    },
    [runEffect],
  )

  // Entering or leaving full screen changes what can hold focus. Entering
  // swaps the rail's toggle for the persistent Exit and puts the dialog shell
  // under inert, so focus parked on Close — where the viewer leaves it on
  // opening — is dropped on the body and the next `f` would never reach the
  // dialog; leaving unmounts Exit. Focus on the control about to go is noted
  // here and handed to its counterpart once it exists (the effect below), and
  // focus that is no longer inside the frame lands there too. Focus anywhere
  // in the frame — the timeline under an `f`, say — stays where it is.
  const handoffRef = useRef(false)
  const setFullscreen = useCallback(
    (next: boolean) => {
      const going = next ? fullscreenToggleRef.current : exitRef.current
      const handoff = going !== null && document.activeElement === going
      handoffRef.current = handoff
      // No browser promises a focusout for a control removed under focus, so
      // the rail's pin is released by hand as its toggle goes.
      if (handoff && next) setHudFocused(false)
      onFullscreenChange(next)
    },
    [onFullscreenChange],
  )

  const settledFullscreenRef = useRef(fullscreen)
  useEffect(() => {
    if (settledFullscreenRef.current === fullscreen) return
    settledFullscreenRef.current = fullscreen
    const handoff = handoffRef.current
    handoffRef.current = false
    const root = rootRef.current
    if (!handoff && root !== null && root.contains(document.activeElement)) return
    ;(fullscreen ? exitRef : fullscreenToggleRef).current?.focus()
  }, [fullscreen])

  useImperativeHandle(
    ref,
    () => ({
      pause() {
        videoRef.current?.pause()
        dispatch({ type: 'playingChange', isPlaying: false })
      },
      shortcut(event) {
        if (event.defaultPrevented) return
        // Any key on the open viewer is activity, whether or not it is mapped.
        dispatch({ type: 'wake' })
        if (event.metaKey || event.ctrlKey || event.altKey) return
        // The timeline's own keys arrive here already handled — `seekKeyDown`
        // prevents their default and the guard above honors it — so the map
        // works from the timeline too without stepping a seek twice. A
        // button's Space is its own activation, never a playback toggle
        // underneath it.
        const onButton = event.target instanceof HTMLButtonElement
        switch (event.key) {
          case ' ':
            if (onButton) return
            dispatch({ type: 'togglePlayback' })
            break
          case 'k':
          case 'K':
            dispatch({ type: 'togglePlayback' })
            break
          case 'ArrowLeft':
            dispatch({ type: 'stepSeek', direction: -1 })
            break
          case 'ArrowRight':
            dispatch({ type: 'stepSeek', direction: 1 })
            break
          case 'm':
          case 'M':
            dispatch({ type: 'toggleSound' })
            break
          case 'f':
          case 'F':
            setFullscreen(!fullscreen)
            break
          default:
            return
        }
        event.preventDefault()
      },
    }),
    [dispatch, fullscreen, setFullscreen],
  )

  // First-frame proof, where the platform can give it: the frame callback
  // fires once a decoded frame has actually been presented. Elsewhere the
  // time-update fallback below stands in.
  useEffect(() => {
    const video = videoRef.current
    if (!video || typeof video.requestVideoFrameCallback !== 'function') return
    let live = true
    const handle = video.requestVideoFrameCallback(() => {
      if (live) dispatch({ type: 'firstFrameRender' })
    })
    return () => {
      live = false
      video.cancelVideoFrameCallback(handle)
    }
  }, [dispatch])

  // Only a playing, idle clip fades its rail; hovering or focusing the rail
  // pins it, and every wake re-arms the wait from the start.
  const mayFade = controlsMayFade(state)
  const hudEngaged = hudHovered || hudFocused
  useEffect(() => {
    if (!mayFade || hudEngaged) return
    let fadeTimer: ReturnType<typeof setTimeout> | undefined
    const idleTimer = setTimeout(() => {
      // Asked at fade time rather than once: the setting can change under a
      // session, and nothing where the reader has asked for less motion.
      const ms = motionMs(VIDEO_CONTROL_FADE_MS)
      if (ms === 0) {
        dispatch({ type: 'controlsFaded' })
        return
      }
      setFading(true)
      fadeTimer = setTimeout(() => dispatch({ type: 'controlsFaded' }), ms)
    }, VIDEO_CONTROL_IDLE_MS)
    return () => {
      clearTimeout(idleTimer)
      if (fadeTimer !== undefined) clearTimeout(fadeTimer)
    }
  }, [state.wakeCount, mayFade, hudEngaged, dispatch])

  useEffect(() => {
    if (focusTransportOnMount) transportRef.current?.focus()
  }, [focusTransportOnMount])

  const reportReady = () => {
    dispatch({ type: 'statusChange', status: 'readyToPlay', duration: videoRef.current?.duration })
  }

  const reportTime = () => {
    const video = videoRef.current
    if (!video) return
    const currentTime = video.currentTime
    dispatch({ type: 'timeUpdate', currentTime, duration: video.duration })
    // Without a frame callback, time advancing under a playing clip is the
    // best proof the platform offers that a frame has been drawn.
    const current = stateRef.current
    if (
      typeof video.requestVideoFrameCallback !== 'function' &&
      !current.firstFrameRendered &&
      current.isPlaying &&
      currentTime > 0
    ) {
      dispatch({ type: 'firstFrameRender' })
    }
  }

  const wakeFromPointer = () => {
    const now = Date.now()
    const asleep = fading || stateRef.current.controlsFaded
    if (!asleep && now - lastPointerWakeRef.current < POINTER_WAKE_THROTTLE_MS) return
    lastPointerWakeRef.current = now
    dispatch({ type: 'wake' })
  }

  const action = surfaceAction(state)
  const sound = soundAction(state)
  const duration = effectiveDuration(state)
  const position = shownPosition(state)
  const loading = showLoading(state)
  const hidden = controlsHidden(state)
  const transportLabel = TRANSPORT_LABELS[action]

  const seekKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        dispatch({ type: 'stepSeek', direction: -1 })
        break
      case 'ArrowRight':
      case 'ArrowUp':
        dispatch({ type: 'stepSeek', direction: 1 })
        break
      case 'Home':
        dispatch({ type: 'seek', position: 0 })
        break
      case 'End':
        dispatch({ type: 'seek', position: duration })
        break
      default:
        return
    }
    event.preventDefault()
  }

  const railBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setHudFocused(false)
  }

  return (
    <div
      ref={rootRef}
      data-media-player=""
      data-media-fullscreen={fullscreen ? '' : undefined}
      className={frameClassName(fullscreen)}
      style={frameStyle(item, fullscreen)}
      onPointerMove={wakeFromPointer}
      onFocus={() => dispatch({ type: 'wake' })}
    >
      {/* makaira-jo8v.18 owns captions/audio alternatives before GA. */}
      <video
        ref={videoRef}
        src={contentSrc}
        poster={posterSrc}
        muted={state.muted}
        playsInline
        preload="metadata"
        aria-label={description}
        tabIndex={-1}
        onLoadedMetadata={reportReady}
        onCanPlay={reportReady}
        onWaiting={() => dispatch({ type: 'statusChange', status: 'loading' })}
        onError={() => dispatch({ type: 'statusChange', status: 'error' })}
        onEmptied={() => dispatch({ type: 'statusChange', status: 'idle' })}
        onPlaying={() => dispatch({ type: 'playingChange', isPlaying: true, status: 'readyToPlay' })}
        // A stall never arrives as `pause` — that is `waiting` — so a `pause`
        // the element reports is always deliberate (the page hidden on iOS, a
        // Media Session control, an audio interruption) and drops the intent
        // whatever the status was, the way `playing` reports ready.
        onPause={() => dispatch({ type: 'playingChange', isPlaying: false, status: 'readyToPlay' })}
        onTimeUpdate={reportTime}
        onDurationChange={() => {
          const video = videoRef.current
          if (video) dispatch({ type: 'durationChange', duration: video.duration })
        }}
        onEnded={() => dispatch({ type: 'playToEnd' })}
        onVolumeChange={() => {
          const video = videoRef.current
          if (video) dispatch({ type: 'mutedChange', muted: video.muted })
        }}
        className="absolute inset-0 h-full w-full object-contain"
      />

      {/* The whole surface answers a tap, but as a picture of the transport
          rather than a second control: the rail's transport is the ONE
          accessible Play / Pause / Replay, so the surface has no role, no
          name and no place in the tree. */}
      <div
        data-media-surface=""
        aria-hidden="true"
        onClick={() => dispatch({ type: 'togglePlayback' })}
        className="absolute inset-0 grid place-items-center text-paper"
      >
        {action === 'play' && (
          <span className="grid h-16 w-16 place-items-center rounded-full border border-paper/80 bg-ink/60">
            <PlayMark className="ml-1 h-7 w-7" />
          </span>
        )}
        {state.ended && (
          <span
            data-media-replay=""
            className="flex min-h-11 items-center gap-2 rounded-full border border-paper bg-paper px-5 text-[13px] font-semibold text-pine"
          >
            <ReplayMark className="h-4 w-4" />
            Replay
          </span>
        )}
      </div>

      {/* The announcement, mounted before it has anything to say so the live
          region is known to the reader by the time it does (WCAG 4.1.3); the
          veil below is only the picture of it. */}
      <p role="status" className="sr-only">
        {announcementFor(loading, state.ended)}
      </p>

      {loading && (
        <div
          data-media-veil=""
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 grid place-items-center text-paper"
        >
          <span className="flex items-center gap-2 rounded-full bg-ink/70 px-3 py-2 text-[12px] font-semibold">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-paper/40 border-t-paper motion-reduce:animate-none" />
            Loading video…
          </span>
        </div>
      )}

      {fullscreen && (
        <button
          ref={exitRef}
          type="button"
          data-media-exit-fullscreen=""
          aria-label="Exit full screen"
          aria-keyshortcuts="f"
          onClick={() => setFullscreen(false)}
          className="absolute left-[max(12px,env(safe-area-inset-left))] top-[max(12px,env(safe-area-inset-top))] z-10 grid h-11 w-11 place-items-center rounded-full border border-gilt bg-ink/70 text-paper"
        >
          <ExitFullscreenMark className="h-4 w-4" />
        </button>
      )}

      {/* Faded is a look: transparent and out of the pointer's way, never
          `aria-hidden` or `inert`, because a browse-mode screen reader's
          navigation keys never reach the page and could not wake it. A
          keyboard loses nothing — the keydown wakes the rail before the
          browser moves focus. */}
      <div
        data-media-hud=""
        onPointerEnter={() => {
          setHudHovered(true)
          // Arriving is a wake as well as a pin: a touch tap in the fade
          // window fires no move first.
          wakeFromPointer()
        }}
        onPointerLeave={() => setHudHovered(false)}
        onFocus={() => setHudFocused(true)}
        onBlur={railBlur}
        className={cn(
          'absolute inset-x-0 bottom-0 flex flex-col gap-1 bg-gradient-to-t from-ink/90 via-ink/55 to-transparent px-3 pb-2 pt-10 text-paper transition-opacity duration-[180ms] motion-reduce:transition-none',
          fullscreen && 'pb-[max(8px,env(safe-area-inset-bottom))]',
          (fading || hidden) && 'opacity-0',
          hidden && 'pointer-events-none',
        )}
      >
        {/* Text on the rail carries its own ink backing: paper on the gradient
            alone falls below 4.5:1 over a bright frame (WCAG 1.4.3). */}
        <div data-media-seek-lane="" className="flex h-11 items-center gap-2">
          <span
            aria-hidden="true"
            className="min-w-[2.6em] rounded-club-sm bg-ink/80 px-1 text-right text-[11px] tabular-nums"
          >
            {formatPlaybackTime(position)}
          </span>
          <input
            type="range"
            className="club-seek min-w-0 flex-1"
            aria-label="Video timeline"
            aria-valuetext={`${formatPlaybackTime(position)} of ${formatPlaybackTime(duration)}`}
            min={0}
            max={duration}
            // Unstepped, and the unrounded position: a one-second step is
            // sanitized DOWN against a fractional duration, parking the thumb
            // short of the fill `--progress` paints to.
            step="any"
            value={position}
            style={{ '--progress': `${progressFraction(state) * 100}%` } as CSSProperties}
            onChange={(event) => dispatch({ type: 'seek', position: Number(event.currentTarget.value) })}
            onKeyDown={seekKeyDown}
          />
          <span
            aria-hidden="true"
            className="min-w-[2.6em] rounded-club-sm bg-ink/80 px-1 text-[11px] tabular-nums"
          >
            {formatPlaybackTime(duration)}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            ref={transportRef}
            type="button"
            aria-label={transportLabel}
            aria-keyshortcuts="k Space"
            onClick={() => dispatch({ type: 'togglePlayback' })}
            className="grid h-11 w-11 place-items-center rounded-full border border-paper bg-paper text-pine"
          >
            {action === 'play' && <PlayMark className="ml-0.5 h-4 w-4" />}
            {action === 'pause' && <PauseMark className="h-4 w-4" />}
            {action === 'replay' && <ReplayMark className="h-4 w-4" />}
          </button>

          {sound === 'unavailable' ? (
            // Information, not an action that cannot be taken: a clip with no
            // sound has nothing to turn on.
            <span className="flex min-h-11 items-center gap-2 rounded-club border border-gilt/60 bg-ink/80 px-3 text-[12px] font-semibold text-paper">
              <SoundMark muted className="h-4 w-4" />
              No audio
            </span>
          ) : (
            <button
              type="button"
              aria-label={sound === 'unmute' ? 'Turn sound on' : 'Turn sound off'}
              aria-keyshortcuts="m"
              onClick={() => dispatch({ type: 'toggleSound' })}
              className="flex min-h-11 items-center gap-2 rounded-club border border-gilt bg-ink/80 px-3 text-[12px] font-semibold text-paper"
            >
              <SoundMark muted={state.muted} className="h-4 w-4" />
              {sound === 'unmute' ? 'Sound on' : 'Sound off'}
            </button>
          )}

          {/* Withheld under the layer: the persistent Exit above is the one
              control named for leaving it. */}
          {!fullscreen && (
            <button
              ref={fullscreenToggleRef}
              type="button"
              aria-label="Enter full screen"
              aria-keyshortcuts="f"
              onClick={() => setFullscreen(true)}
              className="ml-auto flex min-h-11 items-center gap-2 rounded-club border border-gilt bg-ink/80 px-3 text-[12px] font-semibold text-paper"
            >
              <FullscreenMark className="h-4 w-4" />
              Full screen
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
